import { useEffect, useRef, useCallback } from 'react';
import gsap from 'gsap';
import { useLanguage } from '@/contexts/LanguageContext';
import { t } from '@/i18n/translations';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// ─── Geographic helpers ───────────────────────────────────────────────────────
// Slovenia bounding box
const LON_MIN = 13.38, LON_RANGE = 3.24;
const LAT_MAX = 46.88, LAT_RANGE = 1.46;
// Geographic aspect ratio of Slovenia (accounts for cos(lat) longitude compression)
const MAP_RATIO = (LON_RANGE * Math.cos(46 * Math.PI / 180)) / LAT_RANGE; // ≈ 1.534

// Convert lon/lat → normalised map coords (0–1 in both axes, inside bounding box)
const g = (lon, lat) => ({
  x: (lon - LON_MIN) / LON_RANGE,
  y: (LAT_MAX - lat) / LAT_RANGE,
});

// Compute canvas-pixel transform that preserves geographic aspect ratio
// Returns { ox, oy, sx, sy } such that:
//   canvas_x = ox + normX * sx
//   canvas_y = oy + normY * sy
const mapTransform = (w, h, coverage = 0.78) => {
  const scale = Math.min(w * coverage / MAP_RATIO, h * coverage);
  return {
    sx: scale * MAP_RATIO,
    sy: scale,
    ox: (w - scale * MAP_RATIO) / 2,
    oy: (h - scale) / 2,
  };
};

// ─── City dots – population data (approx. 2023) ──────────────────────────────
// radius will be scaled: r = MIN_R + (pop/MAX_POP)^0.5 * SCALE
const CITY_TARGETS = [
  { ...g(14.51, 46.05), pop: 293000, name: 'Ljubljana' },
  { ...g(15.65, 46.56), pop:  95000, name: 'Maribor' },
  { ...g(14.36, 46.24), pop:  55000, name: 'Kranj' },
  { ...g(13.73, 45.55), pop:  52000, name: 'Koper' },
  { ...g(15.26, 46.23), pop:  49000, name: 'Celje' },
  { ...g(15.17, 45.80), pop:  37000, name: 'Novo Mesto' },
  { ...g(13.65, 46.07), pop:  31000, name: 'Nova Gorica' },
  { ...g(15.49, 45.96), pop:  26000, name: 'Krško' },
  { ...g(15.11, 46.36), pop:  26000, name: 'Velenje' },
  { ...g(15.87, 46.42), pop:  23000, name: 'Ptuj' },
  { ...g(16.17, 46.66), pop:  19000, name: 'Murska Sobota' },
  { ...g(15.57, 46.39), pop:  17000, name: 'Sl. Bistrica' },
  { ...g(15.08, 46.51), pop:  16000, name: 'Slovenj Gradec' },
  { ...g(15.05, 46.15), pop:  16000, name: 'Trbovlje' },
  { ...g(14.86, 45.64), pop:  15000, name: 'Kočevje' },
  { ...g(14.30, 46.17), pop:  12000, name: 'Škofja Loka' },
  { ...g(13.73, 46.19), pop:  12000, name: 'Tolmin' },
  { ...g(14.11, 46.37), pop:   8000, name: 'Bled' },
  { ...g(14.21, 45.78), pop:   9000, name: 'Postojna' },
  { ...g(13.78, 46.49), pop:   5000, name: 'Kranjska Gora' },
];
const MAX_POP = 293000;
const cityRadius = (pop) => 1.5 + Math.sqrt(pop / MAX_POP) * 5.5;

// ─── Slovenia border – real OSM data, simplified with RDP (epsilon=0.018) ────
// Source: OpenStreetMap via Nominatim (120 points)
const SLOVENIA_OUTLINE = [
  g(13.37547, 46.29770), g(13.42286, 46.23453), g(13.41144, 46.21180),
  g(13.52907, 46.21890), g(13.56699, 46.18576), g(13.66424, 46.17797),
  g(13.64395, 46.13721), g(13.49651, 46.05850), g(13.50865, 46.03495),
  g(13.47616, 46.00608), g(13.53059, 45.96625), g(13.64298, 45.98289),
  g(13.57517, 45.85350), g(13.59587, 45.80812), g(13.79441, 45.74409),
  g(13.91866, 45.63352), g(13.83905, 45.58227), g(13.62873, 45.63154),
  g(13.38444, 45.56594), g(13.67532, 45.44257), g(13.78643, 45.46646),
  g(13.81470, 45.43417), g(13.88711, 45.42580), g(13.91464, 45.45354),
  g(13.98493, 45.45607), g(13.96221, 45.50599), g(14.00192, 45.51998),
  g(14.13654, 45.47503), g(14.20768, 45.46923), g(14.23726, 45.50579),
  g(14.32277, 45.47041), g(14.37896, 45.48805), g(14.49070, 45.53796),
  g(14.50134, 45.60783), g(14.53502, 45.61391), g(14.57062, 45.67290),
  g(14.60792, 45.66420), g(14.59573, 45.62708), g(14.69032, 45.58068),
  g(14.69058, 45.52662), g(14.79590, 45.50314), g(14.81793, 45.45947),
  g(14.90869, 45.47774), g(14.93151, 45.52929), g(15.08578, 45.48317),
  g(15.17765, 45.42148), g(15.38579, 45.48670), g(15.30199, 45.53562),
  g(15.27696, 45.60434), g(15.40627, 45.64989), g(15.36922, 45.69338),
  g(15.34814, 45.67114), g(15.35711, 45.71397), g(15.32279, 45.67385),
  g(15.28816, 45.69243), g(15.25675, 45.71884), g(15.27478, 45.74005),
  g(15.46997, 45.79535), g(15.46592, 45.81839), g(15.52801, 45.82379),
  g(15.53855, 45.84760), g(15.64227, 45.82042), g(15.70932, 45.84652),
  g(15.67752, 45.86888), g(15.67948, 45.90450), g(15.70730, 45.92183),
  g(15.69985, 45.99703), g(15.73249, 46.04452), g(15.62115, 46.08943),
  g(15.59613, 46.14635), g(15.64719, 46.21717), g(15.76795, 46.20880),
  g(15.79142, 46.25922), g(16.01421, 46.30894), g(16.07743, 46.34597),
  g(16.05814, 46.39358), g(16.30162, 46.37840), g(16.24150, 46.49822),
  g(16.36751, 46.55484), g(16.52196, 46.47817), g(16.59670, 46.47591),
  g(16.52363, 46.50530), g(16.50834, 46.56523), g(16.39177, 46.63373),
  g(16.42863, 46.69397), g(16.36892, 46.70401), g(16.31864, 46.75414),
  g(16.31277, 46.79731), g(16.34770, 46.84071), g(16.23323, 46.87667),
  g(15.98537, 46.82730), g(15.98812, 46.74087), g(16.02982, 46.71161),
  g(16.03808, 46.65614), g(15.83969, 46.72239), g(15.59512, 46.69001),
  g(15.50227, 46.61391), g(15.41407, 46.65554), g(15.04104, 46.65177),
  g(14.97938, 46.60140), g(14.95567, 46.63272), g(14.91957, 46.60208),
  g(14.87040, 46.60722), g(14.81861, 46.50938), g(14.71476, 46.49996),
  g(14.69939, 46.46369), g(14.59597, 46.43682), g(14.56412, 46.37230),
  g(14.52176, 46.42726), g(14.47889, 46.41351), g(14.42961, 46.44704),
  g(14.16345, 46.43357), g(14.09154, 46.48570), g(13.71408, 46.52299),
  g(13.68926, 46.43998), g(13.59775, 46.43938), g(13.56554, 46.39797),
  g(13.43875, 46.36001), g(13.44826, 46.33113), g(13.37547, 46.29770),
];

// ─── Canvas component ─────────────────────────────────────────────────────────
const HeroCanvas = ({ scrollProgress }) => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const initialized = useRef(false);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || initialized.current) return;
    initialized.current = true;

    const ctx = canvas.getContext('2d');
    let w, h, mt;

    const resize = () => {
      w = canvas.width = canvas.parentElement.offsetWidth;
      h = canvas.height = canvas.parentElement.offsetHeight;
      mt = mapTransform(w, h);
    };
    resize();

    // Helper: geo normX/Y → canvas px
    const toCanvas = (pt) => ({
      cx: mt.ox + pt.x * mt.sx,
      cy: mt.oy + pt.y * mt.sy,
    });

    // Build particles: city dots (population-sized) + 28 scatter
    const cityParticles = CITY_TARGETS.map((t) => {
      const { cx, cy } = toCanvas(t);
      const sx = Math.random() * w, sy = Math.random() * h;
      return {
        startX: sx, startY: sy,
        targetX: cx, targetY: cy,
        x: sx, y: sy,
        vx: (Math.random() - 0.5) * 0.6,
        vy: (Math.random() - 0.5) * 0.6,
        r: cityRadius(t.pop),
        isCity: true,
        alpha: 0.9,
      };
    });

    const scatterParticles = Array.from({ length: 28 }, () => {
      const sx = Math.random() * w, sy = Math.random() * h;
      return {
        startX: sx, startY: sy,
        targetX: Math.random() * w,
        targetY: Math.random() * h,
        x: sx, y: sy,
        vx: (Math.random() - 0.5) * 0.6,
        vy: (Math.random() - 0.5) * 0.6,
        r: Math.random() * 1.5 + 0.5,
        isCity: false,
        alpha: 0.4 + Math.random() * 0.3,
      };
    });

    const particles = [...cityParticles, ...scatterParticles];

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      const progress = scrollProgress.current;

      particles.forEach((p) => {
        if (progress < 0.05) {
          p.x += p.vx; p.y += p.vy;
          if (p.x < 0 || p.x > w) p.vx *= -1;
          if (p.y < 0 || p.y > h) p.vy *= -1;
          p.startX = p.x; p.startY = p.y;
        } else {
          const t = Math.min(progress * 1.5, 1);
          const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
          p.x = p.startX + (p.targetX - p.startX) * eased;
          p.y = p.startY + (p.targetY - p.startY) * eased;
        }

        const pAlpha = p.isCity
          ? 0.4 + progress * 0.6
          : (1 - progress * 0.5) * p.alpha;
        const size = p.isCity
          ? p.r * (1 + progress * 0.8)
          : p.r * (1 - progress * 0.3);

        ctx.beginPath();
        ctx.arc(p.x, p.y, Math.max(size, 0.5), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(200,117,51,${pAlpha})`;
        ctx.fill();

        if (p.isCity && progress > 0.5) {
          const ringAlpha = (progress - 0.5) * 0.18;
          ctx.beginPath();
          ctx.arc(p.x, p.y, size * 2.5, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(200,117,51,${ringAlpha})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
      });

      // Connections
      const connDist = progress < 0.3 ? 160 : 160 - progress * 80;
      const connAlpha = progress < 0.5 ? 0.12 : 0.12 * (1 - (progress - 0.5) * 1.5);
      if (connAlpha > 0.01) {
        for (let i = 0; i < particles.length; i++) {
          for (let j = i + 1; j < particles.length; j++) {
            const dx = particles[j].x - particles[i].x;
            const dy = particles[j].y - particles[i].y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < connDist) {
              ctx.beginPath();
              ctx.moveTo(particles[i].x, particles[i].y);
              ctx.lineTo(particles[j].x, particles[j].y);
              ctx.strokeStyle = `rgba(200,117,51,${(1 - dist / connDist) * connAlpha})`;
              ctx.lineWidth = 0.5;
              ctx.stroke();
            }
          }
        }
      }

      // Slovenia outline – drawn with aspect-ratio-correct transform
      if (progress > 0.3) {
        const outlineAlpha = Math.min((progress - 0.3) * 1.5, 0.25);
        ctx.beginPath();
        SLOVENIA_OUTLINE.forEach((pt, i) => {
          const cx = mt.ox + pt.x * mt.sx;
          const cy = mt.oy + pt.y * mt.sy;
          if (i === 0) ctx.moveTo(cx, cy);
          else ctx.lineTo(cx, cy);
        });
        ctx.closePath();
        ctx.strokeStyle = `rgba(255,255,255,${outlineAlpha})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      animRef.current = requestAnimationFrame(draw);
    };

    draw();

    const handleResize = () => {
      resize();
      // Recalculate city targets after resize
      cityParticles.forEach((p, i) => {
        const { cx, cy } = toCanvas(CITY_TARGETS[i]);
        p.targetX = cx;
        p.targetY = cy;
      });
    };
    window.addEventListener('resize', handleResize);

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, [scrollProgress]);

  useEffect(() => {
    const cleanup = initCanvas();
    return cleanup;
  }, [initCanvas]);

  return <canvas ref={canvasRef} className="particle-canvas" />;
};

// ─── Hero section ─────────────────────────────────────────────────────────────
export const HeroSection = () => {
  const sectionRef = useRef(null);
  const scrollProgress = useRef(0);
  const { lang } = useLanguage();
  const tx = t(lang).hero;

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: '+=15%',
          scrub: 0.05,
          pin: true,
          pinSpacing: true,
          onUpdate: (self) => { scrollProgress.current = self.progress; },
        },
      });
      tl.fromTo('.hero-title',    { y: -100 }, { y: 0,   ease: 'power2.out' }, 0);
      tl.fromTo('.hero-subtitle', { y: -100 }, { y: 0,   ease: 'power2.out' }, 0);
      tl.to('.hero-scroll-hint', { opacity: 0, ease: 'none' }, 0);

      gsap.fromTo('.hero-badge',    { opacity: 0, scale: 0.9 }, { opacity: 1, scale: 1,  duration: 0.8, ease: 'back.out(1.5)', delay: 0.2 });
      gsap.fromTo('.hero-cta',      { opacity: 0, y: 20 },       { opacity: 1, y: 0,     duration: 0.8, ease: 'power3.out',    delay: 0.9 });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="hero"
      ref={sectionRef}
      data-testid="hero-section"
      className="scroll-section relative min-h-screen flex items-center justify-center overflow-hidden !py-0"
    >
      <div className="hero-aurora" />
      <HeroCanvas scrollProgress={scrollProgress} />

      {/* Badge – pritrjen visoko v sekciji */}
      <div className="hero-badge absolute top-28 left-1/2 -translate-x-1/2 z-20 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#C87533]/30 bg-[#C87533]/10 whitespace-nowrap">
        <span className="w-2 h-2 rounded-full bg-[#C87533] pulse-dot" />
        <span className="font-mono text-sm text-[#C87533] uppercase tracking-wider">
            {tx.badge}
          </span>
      </div>

      {/* Centrirana vsebina */}
      <div className="section-content relative z-10 max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 text-center pt-24 sm:pt-0">
        <h1 className="hero-title font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-tight mb-3">
          <span className="text-white">{tx.title1}</span>
          <br />
          <span className="text-copper-gradient">{tx.title2}</span>
        </h1>

        <p className="hero-subtitle text-base md:text-lg lg:text-xl text-white/70 max-w-2xl mx-auto leading-relaxed font-body">
          {tx.subtitle}
        </p>
      </div>

      {/* Scroll hint – pritrjen na dno ekrana */}
      <div className="hero-scroll-hint absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2 opacity-60">
        <span className="font-mono text-xs text-white/40 uppercase tracking-wider">{t(lang).nav.scrollHint}</span>
        <svg width="20" height="28" viewBox="0 0 20 28" fill="none" className="animate-bounce">
          <rect x="1" y="1" width="18" height="26" rx="9" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
          <circle cx="10" cy="9" r="3" fill="rgba(200,117,51,0.6)" />
        </svg>
      </div>
    </section>
  );
};
